import { frameLayout } from './fit.js';
import { apply, invert, rectToQuad, DegenerateQuadError } from './homography.js';
/**
 * 点がどのセルにあるか(#152)。
 *
 * ## なぜ描画コアに置くか
 *
 * **セルの位置はホモグラフィの逆写像がないと出せない。** 盤面は写真の遠近に
 * 合わせて台形になっているので、「クアッドを縦に半分」では**セルの境目に
 * ならない** —— 遠い側のセルのほうが画面上では狭い。
 *
 * 呼び出し側(S04・ビューア)に同じ計算を持たせると、**描画とドロップ判定で
 * 別々の答えが出る。** 描画コア側に 1 つだけ置く([ADR-0029](../../../docs/adr/0029-panel-split-grid.md))。
 *
 * ## 座標系はどれでもよい
 *
 * **`point` と `quad` が同じ座標系なら、正規化でも表示座標でもよい。**
 * 盤面の矩形は分割の比率(`grid`)から作られ、クアッドとの対応は射影変換で
 * 決まる —— **座標系の一様な拡大は答えを変えない。**
 *
 * だから S04 は正規化座標のまま判定でき、`DisplayTransform` を渡さずに済む。
 *
 * **純粋関数。** WebGL に触らないので検査できる([ADR-0019](../../../docs/adr/0019-dev-foundation.md))。
 */
/**
 * 点の入っているセルの番号(行優先)。
 *
 * 盤面の外・潰れたクアッドなら `null`。**分割が無ければ、内側なら必ず `0`。**
 *
 * 境界線の真上は**左・上のセル**に入れる(行優先の走査で先に当たるほう)。
 * どちらに入っても実害は無いが、**決まっていないと同じ操作で結果が揺れる。**
 */
export function cellAt(point, quad, grid) {
    const layout = frameLayout(quad, grid);
    if (layout === null)
        return null;
    let inverse;
    try {
        inverse = invert(rectToQuad(layout.panel.width, layout.panel.height, quad));
    }
    catch (error) {
        // 頂点が潰れている間は判定しない(編集中に一時的に起こりうる)。
        if (error instanceof DegenerateQuadError)
            return null;
        throw error;
    }
    const [x, y] = apply(inverse, point);
    if (!Number.isFinite(x) || !Number.isFinite(y))
        return null;
    /*
     * **縁には余裕を持たせる。** 呼び出し側は先にクアッドの内包判定を通して
     * いることが多く、そこで「内側」と出た点が逆写像の丸めでわずかに外へ
     * 出ることがある —— そこで弾くと**枠の縁に落とした操作が失敗する。**
     */
    const tolerance = Math.max(layout.panel.width, layout.panel.height) * 1e-6;
    if (x < -tolerance || x > layout.panel.width + tolerance)
        return null;
    if (y < -tolerance || y > layout.panel.height + tolerance)
        return null;
    for (const cell of layout.cells) {
        const [left, top] = cell.corners[0];
        const [right, bottom] = cell.corners[2];
        if (x >= left - tolerance && x <= right + tolerance) {
            if (y >= top - tolerance && y <= bottom + tolerance)
                return cell.index;
        }
    }
    return null;
}
/**
 * セルの輪郭を `quad` と同じ座標系で返す(#152)。**左上から時計回り。**
 *
 * **ドロップ先を光らせるのに使う。** 分割した盤面では枠全体を光らせても
 * 「どのセルに入るか」が分からない —— [`cellAt`](#cellAt) が返した番号を
 * そのまま渡す。
 *
 * 台形の盤面ではセルの輪郭も台形になる。**画面上で等分にはならない** ——
 * 遠い側のセルほど狭く見えるのが正しい。
 *
 * 範囲外の番号・潰れたクアッドなら `null`。
 */
export function cellOutline(quad, grid, cellIndex) {
    const layout = frameLayout(quad, grid);
    const cell = layout?.cells.find((c) => c.index === cellIndex);
    if (layout === null || cell === undefined)
        return null;
    try {
        const toQuad = rectToQuad(layout.panel.width, layout.panel.height, quad);
        return cell.corners.map((corner) => apply(toQuad, corner));
    }
    catch (error) {
        if (error instanceof DegenerateQuadError)
            return null;
        throw error;
    }
}
//# sourceMappingURL=hit-test.js.map